import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { TablesUpdate } from "@/types/db";

export interface Profile {
  id: string;
  first_name: string | null;
  ms_type: string | null;
  years_since_diagnosis: number | null;
  energy_goals: string[];
  devices: string[];
  onboarding_completed: boolean;
  typical_sleep_hours?: number | null;
  morning_notification_time?: string | null;
  evening_notification_time?: string | null;
  consent_email_updates?: boolean;
  consent_push_reminders?: boolean;
  consent_anonymized_research?: boolean;
  consent_care_team_sharing?: boolean;
  consent_updated_at?: string | null;
  subscription_status: string;
  subscription_end_date: string | null;
  stripe_customer_id: string | null;
  created_at: string;
  updated_at: string;
}

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    if (!user) { setProfile(null); setLoading(false); return; }
    const { data } = await supabase.from("profiles").select("*").eq("id", user.id).single();
    setProfile(data as Profile | null);
    setLoading(false);
  };

  useEffect(() => { fetchProfile(); }, [user]);

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return;
    const { error } = await supabase.from("profiles").update(updates as TablesUpdate<"profiles">).eq("id", user.id);
    if (!error) await fetchProfile();
    return error;
  };

  return { profile, loading, updateProfile, refetch: fetchProfile };
};
